import {Box, Modal} from '@mantine/core';
import React, {useEffect, useState} from 'react';

import FilePreview from './FilePreview';
import {useFilePreviewModalStyles} from './useFilePreviewModalStyles';

interface TextFilePreviewModalProps {
  text: string;
  /** Names the file; its extension picks the preview, for example `.pdb` opens the 3D structure viewer. */
  filename: string;
  opened: boolean;
  onClose: () => void;
  title?: string;
}

/** Previews `text` from an object URL that lives as long as this component is mounted. */
function TextPreview({text, filename}: {text: string; filename: string}): JSX.Element | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    const objectUrl = window.URL.createObjectURL(new Blob([text], {type: 'text/plain'}));
    setUrl(objectUrl);
    return () => {
      window.URL.revokeObjectURL(objectUrl);
    };
  }, [text]);

  return url != null ? <FilePreview url={url} mimeType="" filename={filename} /> : null;
}

/**
 * Full-size preview of file contents held in memory rather than stored as a file. There is no file to
 * open or download, so the modal offers only a way to close it.
 */
export default function TextFilePreviewModal({
  text,
  filename,
  opened,
  onClose,
  title,
}: TextFilePreviewModalProps): JSX.Element {
  const {classes} = useFilePreviewModalStyles();

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title ?? filename}
      closeButtonProps={{'aria-label': 'Close modal'}}
      size="min(1200px, 95vw)"
      centered={true}
      withinPortal={true}
      classNames={{inner: classes.inner, content: classes.content, body: classes.body}}
    >
      <Box className={classes.preview}>
        <TextPreview filename={filename} text={text} />
      </Box>
    </Modal>
  );
}
